import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { debounceTime, takeUntil } from 'rxjs/operators';

import DataSource from 'devextreme/data/data_source';

import { environment } from '../../../../../environments/environment';
import { ApiService } from '../../../../common';
import { BaseComponentOnDestroy } from '../../../../common/epics/base-component-on-destroy';
import { ReportGlobalSearchParams } from '../../../../common/model/report-global-search-params.model';
import { AzSearchStoreExtension } from '../../../az-search-store-extension/az-search-store-extension';
import { ISearchBarType } from '../../../interfaces/global-search';
import { AzSearchStoreExtensionType, IActiveGsAzSearchStoreExtensionInfo } from '../../../models/global-search';
import { GlobalSearchConstants } from '../../../models/global-search-constants';
import { GlobalSearchService, GlobalSearchState } from '../../../services/global-search-service';
import { GlobalSearchBarService } from '../../services/global-search-bar.service';

@Component({
  selector: 'app-global-search',
  templateUrl: './global-search-bar.component.html',
  styleUrls: ['./global-search-bar.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GlobalSearchBarComponent extends BaseComponentOnDestroy implements OnInit, OnDestroy {
  searchControl: UntypedFormControl;
  data: Array<any> = [];
  dataSource: DataSource;
  searchText = '*';
  azstore: AzSearchStoreExtension;
  availableOptions = GlobalSearchConstants.GsSearchBarSearchTypes;
  selectedOption: ISearchBarType;
  azSearchStoreExtensionType = AzSearchStoreExtensionType;
  searchTypeToSearchOn: AzSearchStoreExtensionType;
  showSearchSuggestions = false;
  searchTypeOnActiveStore: AzSearchStoreExtensionType;
  searchTypeOnSuggestionStore: AzSearchStoreExtensionType;

  searchIsDisabled = true;

  private reportApiEndpoint = environment.reportServiceApiEndpoint;

  @ViewChild('searchInputElement', { static: false }) searchInputElement: ElementRef<HTMLInputElement>;

  constructor(
    private cdr: ChangeDetectorRef,
    private globalSearchBarService: GlobalSearchBarService,
    private globalSearchService: GlobalSearchService,
    private apiService: ApiService
  ) {
    super();
  }

  ngOnInit() {
    this.searchControl = new UntypedFormControl('');
    this.globalSearchBarService.searchBoxRefresh$
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(() => {
        this.searchControl.reset('', { emitEvent: false, onlySelf: true });
        if (this.searchInputElement?.nativeElement) {
          this.searchInputElement.nativeElement.focus();
        }
        this.azstore.searchAndResetFacets();
      });

    const activeStoreInfo = GlobalSearchState.ActiveGlobalAzSearchStoreExtensionInfo;
    this.azstore = activeStoreInfo.AzSearchStoreExtension;
    this.searchTypeOnActiveStore = GlobalSearchState.ActiveGlobalAzSearchStoreExtensionInfo.Type;
    this.searchTypeOnSuggestionStore = GlobalSearchState.ActiveGlobalAzSearchStoreExtensionInfo.Type;
    this.selectedOption = this.getAvailableOptionOfType(activeStoreInfo.Type);

    this.azstore.setSuggestCallback(this.suggestCallback);
    this.azstore.setSearchCallback(this.searchCallback);

    GlobalSearchState.ActiveGlobalAzSearchStoreExtensionInfo$
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((azSearchStoreExtensionInfo: IActiveGsAzSearchStoreExtensionInfo) => {
        this.selectedOption = this.getAvailableOptionOfType(azSearchStoreExtensionInfo.Type);
        this.searchTypeOnActiveStore = azSearchStoreExtensionInfo.Type;
        /*
         * The active store is also switched from outside this bar - the chip clicks in
         * GlobalSearchService call activate*GlobalSearchInState directly, without going
         * through setSearchTypeToSearchOn. Realign here or the bar keeps suggesting
         * against the index the user just navigated away from. The identity check makes
         * this a no-op on the setSearchTypeToSearchOn path, where azstore is already the
         * store being emitted.
         */
        if (this.azstore !== azSearchStoreExtensionInfo.AzSearchStoreExtension) {
          this.azstore = azSearchStoreExtensionInfo.AzSearchStoreExtension;
          this.searchTypeOnSuggestionStore = azSearchStoreExtensionInfo.Type;
          this.searchTypeToSearchOn = azSearchStoreExtensionInfo.Type;
          this.azstore.setSuggestCallback(this.suggestCallback);
          this.azstore.setSearchCallback(this.searchCallback);
        }
        this.cdr.detectChanges();
      });
    this.searchControl.valueChanges
      .pipe(debounceTime(200),
        takeUntil(this.isDestroyed$))
      .subscribe(val => {
        this.searchIsDisabled = val?.replace(/ /g, '') === '';
        this.showSearchSuggestions = true;
        this.buildDataSource(val);
      });
  }

  getSearchTypeName() {
    return this.getSearchTypeNameOf(this.searchTypeOnActiveStore);
  }

  /*
   * The suggest request sends the search type in the URL and the field list from
   * this.azstore. setSearchTypeToSearchOn swaps the store synchronously but
   * searchTypeOnActiveStore only catches up when the state observable emits, so
   * resolving the suggest URL from the active store sent one type's fields to the
   * other type's index - Azure rejects the whole request on the first field it does
   * not recognise. searchTypeOnSuggestionStore is assigned alongside the store swap,
   * so it always describes the store the field list came from.
   */
  getSuggestionSearchTypeName() {
    return this.getSearchTypeNameOf(this.searchTypeOnSuggestionStore);
  }

  private getSearchTypeNameOf(searchType: AzSearchStoreExtensionType) {
    if (searchType === AzSearchStoreExtensionType.WorkOrder) {
      return 'AssignmentSearch';
    }
    if (searchType === AzSearchStoreExtensionType.Payment) {
      return 'PaymentSearch';
    }
    if (searchType === AzSearchStoreExtensionType.Transaction) {
      return 'TransactionSearch';
    }
  }

  searchCallback = async (state, postBody): Promise<Response> => {
    const searchType = this.getSearchTypeName();
    const stateFacets = postBody.facets;

    const searchRequest: ReportGlobalSearchParams = {
      search: postBody.search,
      facets: stateFacets,
      filter: postBody.filter,
      orderBy: postBody.orderby,
      top: postBody?.top,
      skip: postBody.skip
    };


    return new Promise(resolve => {
      resolve(
        this.apiService.fetchRequest(`GlobalSearch/${searchType}/search`,
          {
            headers: {
              accept: 'application/json, text/plain, */*',
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'content-type': 'application/json',
            },
            body: JSON.stringify(searchRequest),
            method: 'POST'
          }, this.reportApiEndpoint, false)
      );
    });
  };

  suggestCallback = async (state, postBody): Promise<Response> => {
    const searchType = this.getSuggestionSearchTypeName();

    return await new Promise(resolve => {
      resolve(
        this.apiService.fetchRequest(`GlobalSearch/${searchType}/suggestions?search=${this.searchText}&$select=${postBody.select}`,
          {
            headers: {
              accept: 'application/json, text/plain, */*',
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'content-type': 'application/json',
            },
            method: 'GET'
          }, this.reportApiEndpoint, false)
      );
    });
  };

  search() {
    if(!this.searchIsDisabled){
     if (this.searchTypeOnActiveStore !== this.searchTypeToSearchOn) {
        this.switchSearchType(this.searchTypeToSearchOn);
      }
      this.showSearchSuggestions = false;
      this.azstore.setInput(this.searchText);
      this.globalSearchService.trackedExecuteSearchFromSearchBar();
    }
  }

  hideSuggestionsList() {
    setTimeout(() => {
      this.showSearchSuggestions = false;
      this.cdr.detectChanges();
    }, 300);
  }

  setSearchTypeToSearchOn(searchType: AzSearchStoreExtensionType) {
    this.searchTypeToSearchOn = searchType; // The active store only switches when we execute a search.
    this.searchTypeOnSuggestionStore = searchType; // Tracks this.azstore, swapped below and in the active-store subscription.
    this.selectedOption = this.getAvailableOptionOfType(searchType);

    switch (searchType) {
      case AzSearchStoreExtensionType.WorkOrder: {
        this.azstore = GlobalSearchState.AzSearchStoreExtensionWO;
        break;
      }
      case AzSearchStoreExtensionType.Transaction: {
        this.azstore = GlobalSearchState.AzSearchStoreExtensionTransaction;
        break;
      }
      case AzSearchStoreExtensionType.Payment: {
        this.azstore = GlobalSearchState.AzSearchStoreExtensionPayment;
        break;
      }
    }

    this.azstore.setSuggestCallback(this.suggestCallback);
    this.azstore.setSearchCallback(this.searchCallback);

    if (this.searchTypeToSearchOn !== this.searchTypeOnActiveStore) {
      this.search();
    }
    this.buildDataSource(this.searchControl.value);
  }

  private switchSearchType(searchType: AzSearchStoreExtensionType) {
    switch (searchType) {
      case AzSearchStoreExtensionType.WorkOrder: {
        this.globalSearchService.activateWorkOrderGlobalSearchInState();
        break;
      }
      case AzSearchStoreExtensionType.Transaction: {
        this.globalSearchService.activateTransactionGlobalSearchInState();
        break;
      }
      case AzSearchStoreExtensionType.Payment: {
        this.globalSearchService.activatePaymentGlobalSearchInState();
        break;
      }
    }
  }

  private getAvailableOptionOfType(searchType: AzSearchStoreExtensionType): ISearchBarType {
    return this.availableOptions.find(opt => opt.searchType === searchType);
  }

  private buildDataSource(searchText: string) {
    this.searchText = searchText || '*';
    this.azstore.setSuggestionInput(this.searchText);
    this.globalSearchService.trackedSuggest(this.azstore).then(() => {
      const state = this.azstore.getState();
      this.data = state?.suggestions?.suggestions ?? [];
      if (this.data) {
        this.data.forEach(item => (item.SearchText = item['@search.text']));
      }
      this.cdr.detectChanges();
    });
  }
}
